import React from "react";
import { Table, Tag, Button, Dropdown, Empty, Modal } from "antd";
import {
  MoreOutlined,
  ShoppingCartOutlined,
  CalendarOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { RawMaterialPurchase, RawPurchaseAction } from "../../../Types";
import { PurchaseStatus, PurchasePaymentStatus } from "../../../Types";
import { PurchaseItemsTable } from "./PurchaseItemsTable";
import { PAGE_SIZE } from "../../../Constant";
import { tablePagination } from "../../../components";
import { useSectionAccess } from "../../../Services";

const PAYMENT_STATUS_COLOR: Record<PurchasePaymentStatus, string> = {
  [PurchasePaymentStatus.PAID]: "success",
  [PurchasePaymentStatus.PARTIAL]: "warning",
  [PurchasePaymentStatus.DUE]: "error",
};

interface Props {
  purchases: RawMaterialPurchase[];
  total: number;
  page: number;
  isLoading: boolean;
  onPageChange: (page: number) => void;
  onOpen: (rec: RawMaterialPurchase) => void;
  onDelete: (rec: RawMaterialPurchase) => void;
}

export const RawPurchasesTable: React.FC<Props> = ({
  purchases,
  total,
  page,
  isLoading,
  onPageChange,
  onOpen,
  onDelete,
}) => {
  const { isAllowed } = useSectionAccess("raw_purchases");
  const columns = [
    {
      title: "Date",
      dataIndex: "purchase_date",
      key: "purchase_date",
      render: (v: string) => (
        <span className="text-xs text-gray-600 flex items-center gap-1.5">
          <CalendarOutlined className="text-gray-400" />
          {dayjs(v).format("YYYY-MM-DD")}
        </span>
      ),
    },
    {
      title: "Invoice / PO",
      key: "invoice_po",
      render: (_: any, rec: RawMaterialPurchase) => (
        <div className="flex flex-col">
          <span className="font-medium text-gray-700 text-xs">
            {rec.invoice_no || "—"}
          </span>
          {rec.po_no && (
            <span className="text-[11px] text-gray-400">PO: {rec.po_no}</span>
          )}
        </div>
      ),
    },
    {
      title: "Supplier",
      dataIndex: "supplier_name",
      key: "supplier_name",
      render: (v: string) => (
        <span className="font-medium text-gray-700 text-xs">{v}</span>
      ),
    },
    {
      title: "Grand Total",
      dataIndex: "grand_total",
      key: "grand_total",
      render: (v: number) => (
        <span className="font-semibold text-gray-800 text-sm">
          Rs. {v.toFixed(2)}
        </span>
      ),
    },
    {
      title: "Payments",
      key: "payments",
      render: (_: any, rec: RawMaterialPurchase) => (
        <div className="flex flex-col text-xs">
          <span className="text-green-600">
            Paid: Rs. {rec.paid_amount.toFixed(2)}
          </span>
          {rec.due_amount > 0 ? (
            <span className="text-red-500 font-medium">
              Due: Rs. {rec.due_amount.toFixed(2)}
            </span>
          ) : (
            <span className="text-gray-300">Fully paid</span>
          )}
        </div>
      ),
    },
    {
      title: "Payment",
      dataIndex: "payment_status",
      key: "payment_status",
      render: (s: PurchasePaymentStatus) => (
        <Tag
          color={PAYMENT_STATUS_COLOR[s] ?? "default"}
          className="text-[11px] font-medium"
        >
          {s}
        </Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s: PurchaseStatus) => (
        <Tag
          color={s === PurchaseStatus.COMPLETED ? "cyan" : "gold"}
          className="text-[11px] font-medium"
        >
          {s}
        </Tag>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 48,
      render: (_: any, rec: RawMaterialPurchase) => {
        const isDraft = rec.status === PurchaseStatus.DRAFT;
        const menuItems = [
          {
            key: "view",
            label: isDraft ? "Edit Draft" : "View Details",
            icon: <ShoppingCartOutlined />,
            onClick: () => onOpen(rec),
          },
          ...(isDraft && isAllowed(RawPurchaseAction.Edit)
            ? [
                {
                  key: "delete",
                  label: "Delete Draft",
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: () => {
                    Modal.confirm({
                      title: "Delete Purchase Draft?",
                      content:
                        "This will permanently delete this draft purchase order.",
                      okText: "Delete",
                      okButtonProps: { danger: true },
                      cancelText: "Cancel",
                      onOk: () => onDelete(rec),
                    });
                  },
                },
              ]
            : []),
        ];
        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
              icon={<MoreOutlined />}
              className="text-gray-400 hover:text-gray-700"
            />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <>
      <Table
        dataSource={purchases}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        pagination={tablePagination({
          current: page,
          pageSize: PAGE_SIZE,
          total,
          onChange: onPageChange,
          itemName: "raw purchases",
        })}
        scroll={{ x: 1000 }}
        size="middle"
        expandable={{
          expandedRowRender: (record) => <PurchaseItemsTable record={record} />,
          rowExpandable: (record) => !!record.items && record.items.length > 0,
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No raw purchase invoices logged yet."
            />
          ),
        }}
      />
    </>
  );
};
